/**
 * ⚽ Market Modal — Atlético Nacional
 *
 * Enruta al visitante entre la Tienda Colombia y la Tienda Internacional.
 * El mismo archivo corre en las dos tiendas; el rol se define en la sección.
 *
 * Detección de país (en cascada, con caché en localStorage):
 *   1. API nativa de Shopify: /browsing_context_suggestions.json  → ISO real por IP.
 *   2. Respaldo: zona horaria del navegador (Intl) → solo distingue "país local" vs "fuera".
 * Nunca se redirige automáticamente: la decisión siempre es del visitante.
 *
 * API pública: window.AmMarketModal → { open(), close(), reset(), state() }
 */
(function () {
  "use strict";

  var STORAGE_KEY = "am_market_modal_v2";
  var ARRIVAL_PARAM = "am_mkt"; // marca la llegada desde la tienda hermana
  var FORCE_PARAM = "am_geo"; // QA: fuerza el país detectado sin necesidad de VPN
  var GEO_ENDPOINT = "browsing_context_suggestions.json?country[enabled]=true";
  var FETCH_TIMEOUT = 4000;
  var ABROAD = "__ABROAD__"; // sabemos que NO está en el país local, pero no cuál
  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // De dónde salió el visitante hacia la otra tienda → utm_content
  var UTM_CONTENT = {
    auto: "modal-auto",
    float: "boton-flotante",
    manual: "link-manual"
  };

  var instance = null;

  /* ------------------------------------------------------------ storage */

  function readStore() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(patch) {
    try {
      var data = readStore();
      for (var key in patch) {
        if (Object.prototype.hasOwnProperty.call(patch, key)) data[key] = patch[key];
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      /* modo privado / storage lleno: el componente sigue funcionando sin memoria */
    }
  }

  function isFresh(entry, maxAgeMs) {
    return !!entry && typeof entry.ts === "number" && Date.now() - entry.ts < maxAgeMs;
  }

  /* ------------------------------------------------------------- config */

  function readConfig(root) {
    var flag = function (name) {
      return root.getAttribute(name) === "true";
    };
    var num = function (name, fallback) {
      var value = parseFloat(root.getAttribute(name));
      return isNaN(value) ? fallback : value;
    };

    return {
      role: root.getAttribute("data-role") === "international" ? "international" : "local",
      localIso: (root.getAttribute("data-local-iso") || "CO").trim().toUpperCase(),
      localTimezones: (root.getAttribute("data-local-timezones") || "")
        .split(",")
        .map(function (tz) {
          return tz.trim();
        })
        .filter(Boolean),
      targetUrl: (root.getAttribute("data-target-url") || "").trim(),
      keepPath: flag("data-keep-path"),
      newTab: flag("data-new-tab"),
      autoOpen: flag("data-auto-open"),
      floatEnable: flag("data-float-enable"),
      rememberMs: num("data-remember-days", 30) * 864e5,
      geoCacheMs: num("data-geo-cache-hours", 24) * 36e5,
      localName: root.getAttribute("data-local-name") || "",
      countryFallback: root.getAttribute("data-country-fallback") || "",
      sectionId: root.getAttribute("data-section-id") || "",
      utmEnable: flag("data-utm-enable"),
      utmSource: (root.getAttribute("data-utm-source") || "").trim(),
      utmMedium: (root.getAttribute("data-utm-medium") || "").trim(),
      utmCampaign: (root.getAttribute("data-utm-campaign") || "").trim(),
      eventsEnable: flag("data-events-enable"),
      eventsChannel: root.getAttribute("data-events-channel") || "auto"
    };
  }

  /* ---------------------------------------------------------- analítica */

  /**
   * Envía un evento a GA4. Con "auto" prefiere gtag y cae a dataLayer, para no
   * duplicar la medición cuando la tienda tiene gtag.js y GTM al mismo tiempo.
   * Los nombres van en snake_case, como pide GA4 para eventos personalizados.
   */
  function sendEvent(config, name, params) {
    if (!config.eventsEnable) return;

    var channel = config.eventsChannel;
    var hasGtag = typeof window.gtag === "function";
    var hasLayer = !!(window.dataLayer && typeof window.dataLayer.push === "function");

    if (channel !== "datalayer" && hasGtag) {
      window.gtag("event", name, params);
      return;
    }
    if (channel !== "gtag" && hasLayer) {
      var payload = { event: name };
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) payload[key] = params[key];
      }
      window.dataLayer.push(payload);
    }
  }

  /* ---------------------------------------------------------- detección */

  function detectFromShopify() {
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    var controller = "AbortController" in window ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, FETCH_TIMEOUT)
      : null;

    return fetch(rootUrl + GEO_ENDPOINT, controller ? { signal: controller.signal } : undefined)
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (timer) clearTimeout(timer);
        var country = data && data.detected_values && data.detected_values.country;
        if (!country || !country.handle) return null;
        return {
          iso: String(country.handle).toUpperCase(),
          name: country.name || "",
          src: "shopify"
        };
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        return null;
      });
  }

  function detectFromTimezone(config) {
    var tz = "";
    try {
      tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || "").trim();
    } catch (e) {
      return null;
    }
    if (!tz) return null;
    if (config.localTimezones.indexOf(tz) !== -1) {
      return { iso: config.localIso, name: "", src: "tz" };
    }
    return { iso: ABROAD, name: "", src: "tz" };
  }

  /**
   * QA: ?am_geo=CO fuerza el país detectado. No se cachea, no se persiste y en
   * boot() también salta la memoria de la decisión, para poder probar los dos
   * modos desde cualquier ubicación.
   */
  function forcedGeo() {
    try {
      var raw = new URL(window.location.href).searchParams.get(FORCE_PARAM);
      if (!raw) return null;
      return { iso: String(raw).trim().toUpperCase(), name: "", src: "forced" };
    } catch (e) {
      return null;
    }
  }

  function detect(config) {
    var forced = forcedGeo();
    if (forced) return Promise.resolve(forced);

    var cached = readStore().geo;
    if (isFresh(cached, config.geoCacheMs)) return Promise.resolve(cached);

    return detectFromShopify().then(function (geo) {
      if (!geo) geo = detectFromTimezone(config);
      if (geo && geo.iso) {
        geo.ts = Date.now();
        writeStore({ geo: geo });
      }
      return geo;
    });
  }

  /** ¿El visitante está en la tienda equivocada? */
  function isMisplaced(geo, config) {
    if (!geo || !geo.iso) return false;
    var isLocal = geo.iso === config.localIso;
    return config.role === "international" ? isLocal : !isLocal;
  }

  /* --------------------------------------------------------------- URL */

  /**
   * @param {object} config
   * @param {string} trigger cómo llegó el visitante al botón: define utm_content.
   */
  function buildTargetUrl(config, trigger) {
    var target;
    try {
      target = new URL(config.targetUrl, window.location.origin);
    } catch (e) {
      return null;
    }
    if (!/^https?:$/.test(target.protocol)) return null;

    if (config.keepPath) {
      var basePath = target.pathname.replace(/\/+$/, "");
      target = new URL(basePath + window.location.pathname + window.location.search, target.origin);
    }

    target.searchParams.set(ARRIVAL_PARAM, "1");

    // UTMs: solo cuando cada tienda tiene su propia propiedad de GA4. Si las dos
    // comparten una sola propiedad con medición entre dominios hay que apagarlos,
    // porque un cambio de campaña fuerza una sesión nueva y parte el recorrido.
    if (config.utmEnable) {
      if (config.utmSource) target.searchParams.set("utm_source", config.utmSource);
      if (config.utmMedium) target.searchParams.set("utm_medium", config.utmMedium);
      if (config.utmCampaign) target.searchParams.set("utm_campaign", config.utmCampaign);
      target.searchParams.set("utm_content", UTM_CONTENT[trigger] || UTM_CONTENT.auto);
    }

    return target.toString();
  }

  /**
   * Si el visitante acaba de llegar desde la tienda hermana, respetamos su decisión:
   * no mostramos la modal contraria (evita el efecto ping-pong entre tiendas).
   */
  function consumeArrivalParam() {
    if (window.location.search.indexOf(ARRIVAL_PARAM + "=") === -1) return false;
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has(ARRIVAL_PARAM)) return false;
      url.searchParams.delete(ARRIVAL_PARAM);
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  /* ------------------------------------------------------------ instancia */

  function MarketModal(root) {
    this.root = root;
    this.config = readConfig(root);
    this.overlay = root.querySelector("[data-am-overlay]");
    this.card = root.querySelector(".am-mm-card");
    this.floatBtn = root.querySelector("[data-am-float]");
    this.goBtn = root.querySelector("[data-am-go]");
    this.geo = null;
    this.lastFocus = null;
    this.trigger = "auto";
    this.designMode = !!(window.Shopify && window.Shopify.designMode);

    try {
      this.targetHost = new URL(this.config.targetUrl, window.location.origin).hostname;
    } catch (e) {
      this.targetHost = "";
    }

    this.tokens = [];
    var self = this;
    root.querySelectorAll("[data-am-token]").forEach(function (el) {
      self.tokens.push({ el: el, template: el.textContent });
    });
    // En móvil el texto del botón flotante se oculta: el aria-label es lo único
    // que leen los lectores de pantalla, así que también resuelve el token.
    this.floatLabelTpl = this.floatBtn ? this.floatBtn.getAttribute("aria-label") : null;

    this.onKeydown = this.onKeydown.bind(this);
    this.bind();
    this.boot();
  }

  MarketModal.prototype.bind = function () {
    var self = this;

    if (this.overlay) {
      this.overlay.addEventListener("click", function (event) {
        if (event.target === self.overlay) self.close("dismiss");
      });
    }

    this.root.querySelectorAll("[data-am-close]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.close("dismiss");
      });
    });

    this.root.querySelectorAll("[data-am-stay]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.close("stay");
      });
    });

    if (this.goBtn) {
      this.goBtn.addEventListener("click", function (event) {
        if (self.designMode) {
          // En el editor no navegamos: dejaríamos al merchant fuera de la vista previa.
          event.preventDefault();
          if (window.console) console.info("[Market Modal] Destino:", self.goBtn.href);
          return;
        }
        // Sin preventDefault: navega el <a> de forma nativa. Así funcionan el
        // cmd+clic y el clic central, y no rompemos el decorado de enlaces que
        // hacen GA4/GTM sobre el href en el momento del clic.
        writeStore({ choice: { v: "leave", ts: Date.now() } });
        self.track("market_modal_switch");
        if (self.config.newTab) self.close();
      });
    }

    if (this.floatBtn) {
      this.floatBtn.addEventListener("click", function () {
        self.trigger = "float";
        self.track("market_float_click");
        self.open("float");
      });
    }

  };

  MarketModal.prototype.boot = function () {
    var self = this;

    // En el editor no consultamos memoria ni geolocalización: se previsualiza al
    // seleccionar la sección y el botón flotante se muestra siempre.
    this.updateHref("auto");

    if (this.designMode) {
      this.applyTokens(null);
      this.showFloat();
      return;
    }

    if (consumeArrivalParam()) {
      writeStore({ choice: { v: "stay", ts: Date.now() } });
    }

    var remembered = !forcedGeo() && isFresh(readStore().choice, this.config.rememberMs);

    detect(this.config).then(function (geo) {
      self.geo = geo;
      self.applyTokens(geo);
      if (!isMisplaced(geo, self.config)) return;
      if (!remembered && self.config.autoOpen) {
        self.open();
      } else {
        self.showFloat();
      }
    });
  };

  MarketModal.prototype.applyTokens = function (geo) {
    var name = (geo && geo.name) || "";
    if (!name && geo && geo.iso === this.config.localIso) name = this.config.localName;
    if (!name) name = this.config.countryFallback;
    var fill = function (text) {
      return text.replace(/\[pais\]/gi, name);
    };
    this.tokens.forEach(function (token) {
      token.el.textContent = fill(token.template);
    });
    if (this.floatBtn && this.floatLabelTpl) {
      this.floatBtn.setAttribute("aria-label", fill(this.floatLabelTpl));
    }
  };

  /* ------------------------------------------------------------- modal */

  /** Reescribe el href del CTA con el parámetro funcional y los UTM del caso. */
  MarketModal.prototype.updateHref = function (trigger) {
    if (!this.goBtn) return;
    var url = buildTargetUrl(this.config, trigger || "auto");
    if (url) this.goBtn.href = url;
  };

  MarketModal.prototype.track = function (name, extra) {
    var params = {
      store_role: this.config.role,
      detected_country: (this.geo && this.geo.iso) || "unknown",
      geo_source: (this.geo && this.geo.src) || "none",
      target_store: this.targetHost,
      trigger: this.trigger || "auto"
    };
    for (var key in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, key)) params[key] = extra[key];
    }
    sendEvent(this.config, name, params);
  };

  /** @param {string} [trigger] "auto" | "float" | "manual" */
  MarketModal.prototype.open = function (trigger) {
    if (!this.overlay || this.overlay.classList.contains("is-open")) return;

    this.trigger = trigger || "auto";
    this.updateHref(this.trigger);
    this.track("market_modal_view");

    this.lastFocus = document.activeElement;
    this.overlay.removeAttribute("hidden");
    this.hideFloat();

    var scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) document.documentElement.style.setProperty("padding-right", scrollbar + "px");
    document.documentElement.classList.add("am-mm-lock");

    var overlay = this.overlay;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add("is-open");
      });
    });

    document.addEventListener("keydown", this.onKeydown);

    var focusable = this.card && this.card.querySelector(FOCUSABLE);
    if (focusable) focusable.focus();
  };

  /**
   * @param {string} [reason] "stay" | "dismiss" — se guarda para no volver a
   * abrir la modal automáticamente durante la ventana configurada.
   */
  MarketModal.prototype.close = function (reason) {
    if (!this.overlay || !this.overlay.classList.contains("is-open")) return;

    this.overlay.classList.remove("is-open");
    document.removeEventListener("keydown", this.onKeydown);
    document.documentElement.classList.remove("am-mm-lock");
    document.documentElement.style.removeProperty("padding-right");

    var overlay = this.overlay;
    setTimeout(function () {
      if (!overlay.classList.contains("is-open")) overlay.setAttribute("hidden", "");
    }, 300);

    if (reason && !this.designMode) {
      writeStore({ choice: { v: reason, ts: Date.now() } });
      this.track(reason === "stay" ? "market_modal_stay" : "market_modal_dismiss");
    }

    if (this.lastFocus && typeof this.lastFocus.focus === "function") this.lastFocus.focus();

    if (this.designMode || isMisplaced(this.geo, this.config)) this.showFloat();
  };

  MarketModal.prototype.onKeydown = function (event) {
    if (event.key === "Escape" || event.keyCode === 27) {
      this.close("dismiss");
      return;
    }
    if (event.key !== "Tab" || !this.card) return;

    var nodes = Array.prototype.filter.call(this.card.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ------------------------------------------------------ botón flotante */

  MarketModal.prototype.showFloat = function () {
    if (!this.floatBtn || !this.config.floatEnable) return;
    this.floatBtn.removeAttribute("hidden");
    var btn = this.floatBtn;
    requestAnimationFrame(function () {
      btn.classList.add("is-visible");
    });
  };

  MarketModal.prototype.hideFloat = function () {
    if (!this.floatBtn) return;
    this.floatBtn.classList.remove("is-visible");
  };

  /* ------------------------------------------------------------- arranque */

  function init() {
    var root = document.querySelector("[data-market-modal]");
    if (!root || root.hasAttribute("data-am-ready")) return;
    root.setAttribute("data-am-ready", "");
    instance = new MarketModal(root);

    window.AmMarketModal = {
      open: function () {
        instance.open();
      },
      close: function () {
        instance.close();
      },
      reset: function () {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (e) {}
      },
      state: function () {
        return { geo: instance.geo, config: instance.config, stored: readStore() };
      }
    };
  }

  // Disparador reutilizable en cualquier parte del sitio (menú, footer, enlace propio…).
  document.addEventListener("click", function (event) {
    var target = event.target;
    if (!instance || !target || typeof target.closest !== "function") return;
    if (!target.closest("[data-open-market-modal]")) return;
    event.preventDefault();
    instance.open("manual");
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:select", function (event) {
      if (instance && event.detail && event.detail.sectionId === instance.config.sectionId) {
        instance.open();
      }
    });
    document.addEventListener("shopify:section:deselect", function (event) {
      if (instance && event.detail && event.detail.sectionId === instance.config.sectionId) {
        instance.close();
      }
    });
  }

  // El editor de temas re-renderiza la sección al cambiar settings.
  document.addEventListener("shopify:section:load", function (event) {
    if (event.target && event.target.querySelector("[data-market-modal]")) init();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
